import React, { useState, useEffect } from 'react';
import { getArchiveStats, getAllPosts, getPostsByCategory } from '../../data/postsData';
import ArchiveItem from '../../components/ArchiveItem'; // 引入ArchiveItem组件
import './index.css';

const Archive = () => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState(null);
  const [archiveData, setArchiveData] = useState(null);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('heatmap'); // heatmap, list, stats

  // 热力图相关状态
  const [heatmapData, setHeatmapData] = useState([]);
  const [hoveredDay, setHoveredDay] = useState(null);

  useEffect(() => {
    const loadArchiveData = async () => {
      setLoading(true);
      setTimeout(() => {
        try {
          const data = getArchiveStats();
          setArchiveData(data);
          
          // 优先选择最新年份
          if (data.years.length > 0) {
            const latestYear = data.years[0].year;
            setSelectedYear(latestYear);
            
            // 设置默认月份（有文章的最新月份）
            const yearData = data.years.find(y => y.year === latestYear);
            if (yearData && yearData.months.length > 0) {
              const latestMonth = yearData.months[0].month;
              setSelectedMonth(latestMonth);
            }
          }
          
          // 初始化文章列表
          const allPosts = getAllPosts();
          setFilteredPosts(allPosts);
          if (data && data.monthlyStats) {
            const heatmap = data.monthlyStats.map(month => ({
              year: month.year,
              month: month.month,
              monthName: month.monthName,
              count: month.count,
              views: month.views,
              level: getActivityLevel(month.count)
            }));
            setHeatmapData(heatmap);
          }
        } catch (error) {
          console.error('归档数据加载失败:', error);
          setArchiveData(null);
        } finally {
          setLoading(false);
        }
      }, 500);
    };
    
    loadArchiveData();
  }, []);

  // 根据文章数量确定活动级别
  const getActivityLevel = (count) => {
    if (count === 0) return 0;
    if (count <= 2) return 1;
    if (count <= 5) return 2;
    if (count <= 10) return 3;
    return 4;
  };

  // 处理年份选择
  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedCategory('all');
    setSelectedTag(null);
    
    // 筛选该年份的文章
    const allPosts = getAllPosts();
    const yearPosts = allPosts.filter(post => {
      const postYear = new Date(post.createdAt).getFullYear();
      return postYear === year;
    });
    
    setFilteredPosts(yearPosts);
  };

  // 处理月份选择
  const handleMonthSelect = (year, month) => {
    setSelectedYear(year);
    setSelectedMonth(month);
    setSelectedCategory('all');
    setSelectedTag(null);
    
    const allPosts = getAllPosts();
    const monthPosts = allPosts.filter(post => {
      const postDate = new Date(post.createdAt);
      return postDate.getFullYear() === year && 
             postDate.getMonth() + 1 === month;
    });
    
    setFilteredPosts(monthPosts);
    setViewMode('list');
  };

  // 处理分类选择
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedTag(null);
    
    let filtered = [];
    if (category === 'all') {
      filtered = getAllPosts();
    } else {
      filtered = getPostsByCategory(category);
    }
    
    // 如果已选年份，进一步筛选
    if (selectedYear) {
      filtered = filtered.filter(post => {
        const postYear = new Date(post.createdAt).getFullYear();
        return postYear === selectedYear;
      });
    }
    
    setFilteredPosts(filtered);
    setViewMode('list');
  };

  // 处理标签选择
  const handleTagSelect = (tagName) => {
    setSelectedTag(tagName);
    setSelectedCategory('all');
    
    const allPosts = getAllPosts();
    const tagPosts = allPosts.filter(post => 
      post.tags && post.tags.includes(tagName)
    );
    
    setFilteredPosts(tagPosts);
    setViewMode('list');
  };

  // 清除所有筛选
  const handleClearFilters = () => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedCategory('all');
    setSelectedTag(null);
    setFilteredPosts(getAllPosts());
    setViewMode('heatmap');
  };

  // 获取当前年份数据
  const currentYearData = archiveData?.years?.find(year => year.year === selectedYear);

  // 获取月份名称
  const getMonthName = (month) => {
    const months = ['一月', '二月', '三月', '四月', '五月', '六月', 
                   '七月', '八月', '九月', '十月', '十一月', '十二月'];
    return months[month - 1] || '';
  };

  // 渲染时间线组件
  const renderTimeline = () => {
    if (!archiveData?.years) return null;

    return (
      <div className="archive-timeline">
        <div className="timeline-header">
          <h3>📅 时间线</h3>
          <button 
            className="clear-filters-btn"
            onClick={handleClearFilters}
            title="清除所有筛选"
          >
            🔄 重置
          </button>
        </div>
        
        <div className="timeline-years">
          {archiveData.years.map(year => (
            <div 
              key={year.year}
              className={`timeline-year ${selectedYear === year.year ? 'active' : ''}`}
            >
              <div 
                className="year-header"
                onClick={() => handleYearSelect(year.year)}
              >
                <span className="year-number">{year.year}</span>
                <span className="year-count">{year.count}篇</span>
                <span className="year-arrow">{selectedYear === year.year ? '▼' : '▶'}</span>
              </div>
              
              {selectedYear === year.year && (
                <div className="year-months">
                  {year.months.map(month => (
                    <div 
                      key={month.month}
                      className={`timeline-month ${selectedMonth === month.month ? 'active' : ''}`}
                      onClick={() => handleMonthSelect(year.year, month.month)}
                    >
                      <span className="month-name">{month.monthName}</span>
                      <span className="month-count">{month.count}篇</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 渲染热力图
  const renderHeatmap = () => {
    if (!heatmapData.length) return null;

    // 按年份分组
    const years = [...new Set(heatmapData.map(item => item.year))].sort((a, b) => b - a);

    return (
      <div className="heatmap-container">
        <div className="heatmap-header">
          <h3>🔥 发文热力图</h3>
          <div className="heatmap-legend">
            <span className="legend-label">发文密度：</span>
            {[0, 1, 2, 3, 4].map(level => (
              <div key={level} className="legend-item">
                <div className={`legend-square level-${level}`}></div>
                <span className="legend-text">
                  {level === 0 ? '无' : level === 1 ? '少' : level === 2 ? '中' : level === 3 ? '多' : '很多'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="heatmap-years">
          {years.map(year => {
            const yearData = heatmapData.filter(item => item.year === year);
            return (
              <div key={year} className="heatmap-year">
                <div className="heatmap-year-header">
                  <h4>{year}年</h4>
                  <span className="year-total">
                    {yearData.reduce((sum, item) => sum + item.count, 0)}篇文章
                  </span>
                </div>
                
                <div className="heatmap-months">
                  {yearData.map(month => (
                    <div 
                      key={`${year}-${month.month}`}
                      className={`heatmap-month level-${month.level} ${
                        selectedYear === year && selectedMonth === month.month ? 'selected' : ''
                      } ${
                        hoveredDay?.year === year && hoveredDay?.month === month.month ? 'hovered' : ''
                      }`}
                      onClick={() => handleMonthSelect(year, month.month)}
                      onMouseEnter={() => setHoveredDay({year, month: month.month})}
                      onMouseLeave={() => setHoveredDay(null)}
                      title={`${year}年${month.monthName}：${month.count}篇文章，${month.views}次阅读`}
                    >
                      <div className="month-tooltip">
                        <div className="tooltip-month">{month.monthName}</div>
                        <div className="tooltip-count">{month.count}篇文章</div>
                        <div className="tooltip-views">{month.views}次阅读</div>
                      </div>
                      <span className="month-name">{month.monthName}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // 渲染筛选面板
  const renderFilterPanel = () => {
    if (!archiveData) return null;

    return (
      <div className="filter-panel">
        <div className="filter-section">
          <h4>📁 分类筛选</h4>
          <div className="filter-categories">
            <button
              className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => handleCategorySelect('all')}
            >
              全部 ({archiveData.total})
            </button>
            {archiveData.byCategory.map(cat => (
              <button
                key={cat.category}
                className={`filter-btn ${selectedCategory === cat.category ? 'active' : ''}`}
                onClick={() => handleCategorySelect(cat.category)}
              >
                {cat.category} ({cat.count})
              </button>
            ))}
          </div>
        </div>

        <div className="filter-section">
          <h4>🏷️ 热门标签</h4>
          <div className="filter-tags">
            {archiveData.popularTags.slice(0, 10).map(tag => (
              <button
                key={tag.name}
                className={`filter-tag ${selectedTag === tag.name ? 'active' : ''}`}
                onClick={() => handleTagSelect(tag.name)}
                style={{
                  fontSize: tag.size === 'lg' ? '1.1em' : tag.size === 'md' ? '1em' : '0.9em',
                  padding: tag.size === 'lg' ? '8px 16px' : tag.size === 'md' ? '6px 12px' : '4px 10px'
                }}
              >
                #{tag.name} ({tag.count})
              </button>
            ))}
          </div>
        </div>

        <div className="filter-section">
          <h4>👁️ 视图模式</h4>
          <div className="view-mode-selector">
            <button
              className={`view-mode-btn ${viewMode === 'heatmap' ? 'active' : ''}`}
              onClick={() => setViewMode('heatmap')}
            >
              🔥 热力图
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              📝 文章列表
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'stats' ? 'active' : ''}`}
              onClick={() => setViewMode('stats')}
            >
              📊 数据统计
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 渲染文章列表（简化：调用ArchiveItem组件）
  const renderArticleList = () => {
    if (filteredPosts.length === 0) {
      return (
        <div className="no-articles">
          <div className="no-articles-icon">📝</div>
          <h3>没有找到符合条件的文章</h3>
          <p>尝试调整筛选条件或查看所有文章</p>
          <button className="btn btn-primary" onClick={handleClearFilters}>
            查看所有文章
          </button>
        </div>
      );
    }

    return (
      <div className="article-list-container">
        <div className="list-header">
          <h3>
            {selectedTag ? `标签：${selectedTag}` : 
             selectedCategory !== 'all' ? `分类：${selectedCategory}` :
             selectedYear ? (
               <>
                 {selectedYear}年文章
                 {currentYearData && (
                   <span className="year-stats-mini">
                     · {currentYearData.count}篇 · {currentYearData.months.filter(m => m.count > 0).length}个活跃月份
                   </span>
                 )}
               </>
             ) : 
             selectedMonth ? `${selectedYear}年${getMonthName(selectedMonth)}` : 
             '所有文章'}
            <span className="list-count">（{filteredPosts.length}篇）</span>
          </h3>
          <div className="list-sort">
            <select className="sort-select">
              <option>按时间倒序</option>
              <option>按时间正序</option>
              <option>按阅读量</option>
              <option>按点赞数</option>
            </select>
          </div>
        </div>

        <div className="article-list">
          {/* 核心修改：循环渲染ArchiveItem组件 */}
          {filteredPosts.map(post => (
            <ArchiveItem 
              key={post.id} 
              post={post} 
              getMonthName={getMonthName} 
              handleTagSelect={handleTagSelect} 
            />
          ))}
        </div>
      </div>
    );
  };

  // 渲染统计数据
  const renderStats = () => {
    if (!archiveData) return null;

    return (
      <div className="stats-container">
        <div className="stats-header">
          <h3>📊 数据统计</h3>
          <div className="stats-period">
            {selectedYear ? `${selectedYear}年统计` : '全站统计'}
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📝</div>
            <div className="stat-value">
              {selectedYear && currentYearData ? currentYearData.count : archiveData.total}
            </div>
            <div className="stat-label">
              {selectedYear ? '本年文章数' : '总文章数'}
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-value">
              {selectedYear && currentYearData 
                ? currentYearData.months.filter(m => m.count > 0).length 
                : archiveData.years.length}
            </div>
            <div className="stat-label">
              {selectedYear ? '活跃月份' : '跨越年份'}
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">🏷️</div>
            <div className="stat-value">
              {selectedYear && currentYearData 
                ? archiveData.popularTags.filter(tag => 
                    getAllPosts().some(post => 
                      post.tags?.includes(tag.name) && 
                      new Date(post.createdAt).getFullYear() === selectedYear
                    )
                  ).length
                : new Set(archiveData.popularTags.map(t => t.name)).size}
            </div>
            <div className="stat-label">
              {selectedYear ? '本年标签' : '使用标签'}
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">📁</div>
            <div className="stat-value">
              {selectedYear && currentYearData
                ? new Set(
                    getAllPosts()
                      .filter(post => new Date(post.createdAt).getFullYear() === selectedYear)
                      .map(post => post.category)
                  ).size
                : archiveData.byCategory.length}
            </div>
            <div className="stat-label">
              {selectedYear ? '本年分类' : '文章分类'}
            </div>
          </div>
        </div>

        <div className="stats-details">
          {currentYearData && (
            <div className="detail-section">
              <h4>
                {selectedYear}年月份分布
                <span className="section-subtitle">
                  （共 {currentYearData.count} 篇文章）
                </span>
              </h4>
              <div className="year-month-stats">
                {currentYearData.months.map(month => (
                  <div key={month.month} className="month-stat-row">
                    <div className="month-stat-label">{month.monthName}</div>
                    <div className="month-stat-bar">
                      <div 
                        className="month-stat-fill"
                        style={{ 
                          width: currentYearData.count > 0 
                            ? `${(month.count / Math.max(...currentYearData.months.map(m => m.count))) * 100}%` 
                            : '0%'
                        }}
                      ></div>
                    </div>
                    <div className="month-stat-value">
                      {month.count}篇
                      {currentYearData.count > 0 && (
                        <span className="month-stat-percent">
                          ({((month.count / currentYearData.count) * 100).toFixed(1)}%)
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="detail-section">
            <h4>
              {selectedYear ? `${selectedYear}年分类分布` : '全站分类比例'}
              {currentYearData && (
                <span className="section-subtitle">
                  （{currentYearData.count}篇文章）
                </span>
              )}
            </h4>
            <div className="category-percentages">
              {archiveData.byCategory.map(cat => {
                // 计算当前年份的分类数量
                const yearCategoryCount = selectedYear && currentYearData
                  ? getAllPosts().filter(post => 
                      post.category === cat.category && 
                      new Date(post.createdAt).getFullYear() === selectedYear
                    ).length
                  : cat.count;
                
                const totalCount = selectedYear && currentYearData 
                  ? currentYearData.count 
                  : archiveData.total;
                
                return (
                  <div key={cat.category} className="category-percent">
                    <div className="percent-label">{cat.category}</div>
                    <div className="percent-bar">
                      <div 
                        className="percent-fill"
                        style={{ 
                          width: totalCount > 0 
                            ? `${(yearCategoryCount / totalCount) * 100}%` 
                            : '0%' 
                        }}
                      ></div>
                    </div>
                    <div className="percent-value">
                      {totalCount > 0 
                        ? `${((yearCategoryCount / totalCount) * 100).toFixed(1)}%`
                        : '0%'
                      }
                      <span className="percent-count">({yearCategoryCount}篇)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {!selectedYear && (
            <div className="detail-section">
              <h4>年度分布</h4>
              <div className="year-bars">
                {archiveData.years.map(year => (
                  <div key={year.year} className="year-bar">
                    <div 
                      className="bar-label clickable"
                      onClick={() => handleYearSelect(year.year)}
                      title={`点击查看${year.year}年文章`}
                    >
                      {year.year}
                    </div>
                    <div className="bar-container">
                      <div 
                        className="bar-fill"
                        style={{ 
                          width: `${(year.count / Math.max(...archiveData.years.map(y => y.count))) * 100}%` 
                        }}
                      ></div>
                    </div>
                    <div className="bar-count">{year.count}篇</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="archive-loading">
        <div className="loading-content">
          <span className="loading-dog">🐕</span>
          <p>正在整理归档...</p>
        </div>
      </div>
    );
  }

  if (!archiveData) {
    return (
      <div className="archive-error">
        <h2>📂 归档数据加载失败</h2>
        <p>请稍后重试</p>
      </div>
    );
  }

  return (
    <div className="archive-page fade-in">
      {/* 页面头部 */}
      <div className="archive-header">
        <h1>📚 文章归档</h1>
        <p className="archive-subtitle">
          共收录 {archiveData.total} 篇文章，涵盖 {archiveData.years.length} 个年份
        </p>
        <div className="archive-summary">
          <div className="summary-item">
            <span className="summary-icon">📅</span>
            <div>
              <h3>{archiveData.years.length}</h3>
              <p>个年份</p>
            </div>
          </div>
          <div className="summary-item">
            <span className="summary-icon">📝</span>
            <div>
              <h3>{archiveData.total}</h3>
              <p>篇文章</p>
            </div>
          </div>
          <div className="summary-item">
            <span className="summary-icon">🏷️</span>
            <div>
              <h3>{new Set(archiveData.popularTags.map(t => t.name)).size}</h3>
              <p>个标签</p>
            </div>
          </div>
          <div className="summary-item">
            <span className="summary-icon">👁️</span>
            <div>
              <h3>{archiveData.monthlyStats.reduce((sum, month) => sum + month.views, 0).toLocaleString()}</h3>
              <p>次阅读</p>
            </div>
          </div>
        </div>
      </div>

      {/* 两栏布局 */}
      <div className="archive-layout">
        {/* 左侧栏 */}
        <div className="archive-sidebar">
          {renderTimeline()}
          {renderFilterPanel()}
          
          {/* 热门标签 */}
          <div className="sidebar-tags">
            <h4>🔥 最热标签</h4>
            <div className="sidebar-tags-cloud">
              {archiveData.popularTags.slice(0, 8).map(tag => (
                <span 
                  key={tag.name}
                  className={`sidebar-tag ${selectedTag === tag.name ? 'active' : ''}`}
                  onClick={() => handleTagSelect(tag.name)}
                  style={{
                    '--tag-color': tag.size === 'lg' ? '#FF6B9D' : 
                                   tag.size === 'md' ? '#4DB5FF' : '#A8E6CF'
                  }}
                >
                  #{tag.name}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* 右侧主内容区 */}
        <div className="archive-main">
          {/* 当前筛选状态 */}
          <div className="filter-status">
            {selectedYear && currentYearData && (
              <span className="status-tag year-tag" onClick={() => handleYearSelect(selectedYear)}>
                <span className="tag-year">{selectedYear}年</span>
                <span className="tag-count">{currentYearData.count}篇</span>
                <span className="tag-close">✕</span>
              </span>
            )}
            {selectedMonth && (
              <span className="status-tag" onClick={() => setSelectedMonth(null)}>
                月份：{getMonthName(selectedMonth)} ✕
              </span>
            )}
            {selectedCategory !== 'all' && (
              <span className="status-tag" onClick={() => handleCategorySelect('all')}>
                分类：{selectedCategory} ✕
              </span>
            )}
            {selectedTag && (
              <span className="status-tag" onClick={() => setSelectedTag(null)}>
                标签：{selectedTag} ✕
              </span>
            )}
            {(selectedYear || selectedMonth || selectedCategory !== 'all' || selectedTag) && (
              <button className="clear-all-btn" onClick={handleClearFilters}>
                清除所有筛选
              </button>
            )}
          </div>

          {/* 视图切换 */}
          <div className="view-tabs">
            <button 
              className={`view-tab ${viewMode === 'heatmap' ? 'active' : ''}`}
              onClick={() => setViewMode('heatmap')}
            >
              <span className="tab-icon">🔥</span>
              热力图
            </button>
            <button 
              className={`view-tab ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <span className="tab-icon">📝</span>
              文章列表 ({filteredPosts.length})
            </button>
            <button 
              className={`view-tab ${viewMode === 'stats' ? 'active' : ''}`}
              onClick={() => setViewMode('stats')}
            >
              <span className="tab-icon">📊</span>
              数据统计
            </button>
          </div>

          {/* 视图内容 */}
          <div className="view-content">
            {viewMode === 'heatmap' && renderHeatmap()}
            {viewMode === 'list' && renderArticleList()}
            {viewMode === 'stats' && renderStats()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Archive;